import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "../styles/WelcomeTour.css";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function HelpdeskWelcomeTour({
  open,
  onClose,
  onDontShowAgain, // optional
  onOpenSidebar, // optional: force sidebar open
  isEmployer,
  isHelpdeskUser, // employees/agents/managers
}) {
  const [step, setStep] = useState(0);
  const [spot, setSpot] = useState(null);
  const [dontShow, setDontShow] = useState(false);

  const cardRef = useRef(null);
  const lastActiveEl = useRef(null);
  const ranOnEnterForStep = useRef(null);

  const steps = useMemo(() => {
    const base = [
      {
        id: "intro",
        title: "Welcome to Oiiko Help Desk",
        desc:
          "Quick tour: learn where tickets, your work, and where SLA performance lives. You can skip anytime.",
        type: "modal",
        illustration: "rocket",
      },
      {
        id: "create",
        title: "Create a Ticket",
        desc:
          "Click the + button to submit a new IT issue with category, impact, urgency, and optional screenshot.",
        type: "spot",
        // ✅ your navbar create button (shown when canCreateTicket)
        target: ".helpdesknav-icon-button[aria-label='Create Ticket']",
        illustration: "check",
      },
      {
        id: "profile",
        title: "Your Profile",
        desc: "Use the profile icon to view your account info.",
        type: "spot",
        target:
          ".helpdesknav-icon-button[aria-label='Create Ticket'] ~ .helpdesknav-icon-button[aria-haspopup='dialog']",
        illustration: "user",
      },
      {
        id: "sidebar",
        title: "Navigation",
        desc:
          "Use the sidebar to move between Open Tickets, Your Submitted Tickets, Claimed Tickets, and Resolved Tickets.",
        type: "spot",
        target: ".sidebar-toggle-btn",
        onEnter: async () => {
          await Promise.resolve(onOpenSidebar?.());
          await wait(120);
        },
        illustration: "sidebar",
      },
      {
        id: "home",
        title: "Home dashboard",
        desc:
          "Home shows what you’re working on right now and what you resolved this week.",
        type: "spot",
        // highlight the Home tab item
        target: ".hd-side-item[aria-label='Home']",
        illustration: "bell",
      },
    ];

    // Employer-only step (SLA dashboard + settings)
    const employerExtras = [
      {
        id: "sla",
        title: "SLA Performance",
        desc:
          "Track response/resolution performance and identify breached or at-risk tickets.",
        type: "spot",
        target: ".hd-side-item[aria-label='SLA Dashboard']",
        illustration: "bell",
      },
      {
        id: "slaSettings",
        title: "SLA Settings",
        desc:
          "Use the settings gear to configure SLA targets and warning thresholds for the company.",
        type: "spot",
        target: ".helpdesknav-icon-button[title='Settings']",
        illustration: "check",
      },
    ];

    // Only include SLA-related steps if employer (or if you later want managers too)
    if (isEmployer) return [...base, ...employerExtras];
    return base;
  }, [isEmployer, onOpenSidebar]);

  const placeSpotlight = useCallback(async () => {
    const s = steps[step];
    if (!s || s.type !== "spot" || !s.target) {
      setSpot(null);
      return;
    }

    let el = null;
    for (let i = 0; i < 8; i++) {
      el = document.querySelector(s.target);
      if (el) break;
      await wait(50);
      if (steps[step] !== s) return;
    }

    if (!el) {
      setSpot(null);
      return;
    }

    const r = el.getBoundingClientRect();
    const pad = 8;
    const radius = Math.min(14, Math.max(8, Math.min(r.width, r.height) / 4));
    setSpot({
      left: Math.max(0, r.left - pad + window.scrollX),
      top: Math.max(0, r.top - pad + window.scrollY),
      width: r.width + pad * 2,
      height: r.height + pad * 2,
      radius,
    });
  }, [step, steps]);

  useEffect(() => {
    if (!open) return;

    const s = steps[step];
    (async () => {
      if (
        typeof s?.onEnter === "function" &&
        ranOnEnterForStep.current !== s.id
      ) {
        ranOnEnterForStep.current = s.id;
        await s.onEnter();
      }
      await placeSpotlight();
    })();

    const handler = () => placeSpotlight();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, step, steps, placeSpotlight]);

  useEffect(() => {
    if (open) {
      lastActiveEl.current = document.activeElement;
      setTimeout(() => cardRef.current?.focus(), 0);
      const onKey = (e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          next();
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          prev();
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    } else {
      setStep(0);
      setSpot(null);
      ranOnEnterForStep.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    onDontShowAgain?.(!!dontShow);
    onClose?.();
    if (lastActiveEl.current?.focus)
      setTimeout(() => lastActiveEl.current.focus(), 0);
  };

  const next = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!open) return null;

  const cur = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;

  return (
    <div className="wt-overlay" role="dialog" aria-modal="true">
      <div className="wt-backdrop" aria-hidden="true" />
      {spot && (
        <div
          className="wt-spotlight"
          style={{
            left: `${spot.left}px`,
            top: `${spot.top}px`,
            width: `${spot.width}px`,
            height: `${spot.height}px`,
            borderRadius: `${spot.radius}px`,
          }}
          aria-hidden="true"
        />
      )}

      <section className="wt-card" ref={cardRef} tabIndex={-1}>
        <header className="wt-head">
          <div className="wt-brand">
            <div className="wt-logo" aria-hidden="true">
              <span />
            </div>
            <div className="wt-meta">
              <h2 className="wt-title">Help Desk Tour</h2>
              <div className="wt-plan-badge">
                {isEmployer ? "Employer" : "Employee"}
              </div>
            </div>
          </div>

          <button
            className="wt-icon-btn"
            aria-label="Close tour"
            onClick={handleClose}
          >
            ×
          </button>
        </header>

        <div className="wt-body">
          <div className="wt-illustration" aria-hidden="true">
            {cur.illustration && <Illustration type={cur.illustration} />}
          </div>
          <div className="wt-copy">
            <div className="wt-step-eyebrow">
              Step {step + 1} <span>of {steps.length}</span>
            </div>
            <h3 className="wt-step-title">{cur.title}</h3>
            <p className="wt-step-desc">{cur.desc}</p>
          </div>
        </div>

        <footer className="wt-foot">
          <div className="wt-progress">
            {steps.map((_, i) => (
              <button
                key={i}
                className={`wt-dot ${i === step ? "active" : ""}`}
                aria-label={`Go to step ${i + 1}`}
                onClick={() => i !== step && setStep(i)}
              />
            ))}
          </div>

          <div className="wt-actions">
            <button className="wt-btn ghost" onClick={handleClose}>
              Skip
            </button>
            <>
              <button
                className="wt-btn"
                onClick={prev}
                disabled={isFirst}
                aria-disabled={isFirst}
              >
                Back
              </button>
              <button
                className="wt-btn primary"
                onClick={isLast ? handleClose : next}
              >
                {isLast ? "Finish" : "Next"}
              </button>
            </>
          </div>
        </footer>

        <div className="wt-opt-container">
          <label
            className="wt-opt"
            title="Don’t show this tour automatically again"
          >
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
            />
            Don’t show again
          </label>
        </div>
      </section>
    </div>
  );
}

function Illustration({ type }) {
  switch (type) {
    case "rocket":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M38 6c9 3 14 10 14 20 0 6-2 10-6 14l-9 9-7 7-6-6 7-7 9-9c4-4 6-8 6-14 0-5-2-9-8-14z"
            fill="currentColor"
            opacity="0.15"
          />
          <circle cx="36" cy="18" r="4" fill="currentColor" />
        </svg>
      );

    case "bell":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M12 44h40l-4-6V28a16 16 0 10-32 0v10l-4 6z"
            fill="currentColor"
            opacity="0.15"
          />
          <circle cx="32" cy="52" r="4" fill="currentColor" />
        </svg>
      );

    case "user":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="22" r="10" fill="currentColor" opacity="0.2" />
          <rect
            x="16"
            y="36"
            width="32"
            height="16"
            rx="8"
            fill="currentColor"
            opacity="0.15"
          />
        </svg>
      );

    case "sidebar":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <rect
            x="8"
            y="12"
            width="16"
            height="40"
            rx="4"
            fill="currentColor"
            opacity="0.2"
          />
          <rect
            x="28"
            y="12"
            width="28"
            height="40"
            rx="6"
            fill="currentColor"
            opacity="0.12"
          />
        </svg>
      );

    case "check":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.12" />
          <path
            d="M22 33l6 6 14-14"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      );

    // ✅ NEW: Ticket icon (Create a ticket)
    case "ticket":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M14 22a6 6 0 016-6h24a6 6 0 016 6v6a4 4 0 000 8v6a6 6 0 01-6 6H20a6 6 0 01-6-6v-6a4 4 0 000-8v-6z"
            fill="currentColor"
            opacity="0.14"
          />
          <path
            d="M26 24h20M26 32h14M26 40h18"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.5"
          />
        </svg>
      );

    // ✅ NEW: SLA / speedometer icon
    case "sla":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M32 14c-12 0-22 10-22 22 0 10 7 18 16 21h12c9-3 16-11 16-21 0-12-10-22-22-22z"
            fill="currentColor"
            opacity="0.12"
          />
          <path
            d="M32 34l10-8"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.55"
          />
          <circle cx="32" cy="34" r="3" fill="currentColor" />
        </svg>
      );

    // ✅ NEW: Gear (Settings)
    case "gear":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="32" r="8" fill="currentColor" opacity="0.25" />
          <g opacity="0.2">
            <rect x="30" y="4" width="4" height="10" />
            <rect x="30" y="50" width="4" height="10" />
            <rect x="4" y="30" width="10" height="4" />
            <rect x="50" y="30" width="10" height="4" />
            <rect
              x="46"
              y="10"
              width="4"
              height="10"
              transform="rotate(45 48 15)"
            />
            <rect
              x="14"
              y="44"
              width="4"
              height="10"
              transform="rotate(45 16 49)"
            />
            <rect
              x="10"
              y="10"
              width="4"
              height="10"
              transform="rotate(-45 12 15)"
            />
            <rect
              x="42"
              y="44"
              width="4"
              height="10"
              transform="rotate(-45 44 49)"
            />
          </g>
        </svg>
      );

    // ✅ NEW: Home icon (dashboard)
    case "home":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M12 30l20-16 20 16v20a6 6 0 01-6 6H18a6 6 0 01-6-6V30z"
            fill="currentColor"
            opacity="0.14"
          />
          <path
            d="M26 56V38a6 6 0 0112 0v18"
            stroke="currentColor"
            strokeWidth="3"
            opacity="0.45"
          />
        </svg>
      );

    default:
      return null;
  }
}
